import type React from "react";

import type { LanguageDto } from "@/api/types";
import { FlagIconsFlag } from "@/ui-adapters/FlagIconsFlag";
import { RuntimeFlagGeometry } from "@/ui-adapters/RuntimeFlagGeometry";

// https://flagicons.lipis.dev/
const flagRegionMap: Record<string, string> = {
  ace: "ID",
  af: "ZA",
  an: "ES",
  as: "IN",
  ay: "BO",
  az: "AZ",
  ba: "RU",
  be: "BY",
  bho: "IN",
  bg: "BG",
  bn: "BD",
  br: "FR",
  bs: "BA",
  ca: "ES",
  ceb: "PH",
  cs: "CZ",
  cy: "GB",
  da: "DK",
  de: "DE",
  el: "GR",
  en: "GB",
  es: "ES",
  et: "EE",
  eu: "ES",
  fi: "FI",
  fr: "FR",
  ga: "IE",
  gl: "ES",
  gn: "PY",
  gom: "IN",
  gu: "IN",
  ha: "NG",
  hi: "IN",
  hr: "HR",
  ht: "HT",
  hu: "HU",
  hy: "AM",
  id: "ID",
  ig: "NG",
  is: "IS",
  it: "IT",
  ja: "JP",
  jv: "ID",
  ka: "GE",
  kk: "KZ",
  ko: "KR",
  ky: "KG",
  lb: "LU",
  lmo: "IT",
  ln: "CD",
  lt: "LT",
  lv: "LV",
  mai: "IN",
  mg: "MG",
  mi: "NZ",
  mk: "MK",
  ml: "IN",
  mn: "MN",
  mr: "IN",
  ms: "MY",
  mt: "MT",
  my: "MM",
  nb: "NO",
  ne: "NP",
  nl: "NL",
  oc: "FR",
  om: "ET",
  pa: "IN",
  pag: "PH",
  pam: "PH",
  pl: "PL",
  pt: "PT",
  qu: "PE",
  ro: "RO",
  ru: "RU",
  sa: "IN",
  scn: "IT",
  sk: "SK",
  sl: "SI",
  sq: "AL",
  sr: "RS",
  st: "LS",
  su: "ID",
  sv: "SE",
  sw: "TZ",
  ta: "IN",
  te: "IN",
  tg: "TJ",
  th: "TH",
  tk: "TM",
  tl: "PH",
  tn: "BW",
  tr: "TR",
  ts: "ZA",
  tt: "RU",
  uk: "UA",
  uz: "UZ",
  vi: "VN",
  wo: "SN",
  xh: "ZA",
  yue: "HK",
  zh: "CN",
  zu: "ZA",
};

function getFlagRegion(languageId: string): string | undefined {
  return Object.hasOwn(flagRegionMap, languageId) ? flagRegionMap[languageId]?.toLowerCase() : undefined;
}

/** Renders a language flag, representative regional flag, or compact language-code fallback. */
export function FlagIcon({ size = 16, languageId }: { size?: number; languageId: LanguageDto["id"] }): React.ReactNode {
  const flagRegion = getFlagRegion(languageId);

  if (flagRegion) {
    return <FlagIconsFlag region={flagRegion} size={size} />;
  }

  return (
    <RuntimeFlagGeometry fallback size={size}>
      {(runtimeFlagAttributes) => (
        <span
          {...runtimeFlagAttributes}
          aria-hidden="true"
          className="inline-flex shrink-0 items-center justify-center rounded-sm border border-border-secondary bg-background-secondary font-semibold text-text-secondary uppercase"
          data-testid="language-indicator"
        >
          {languageId.slice(0, 3)}
        </span>
      )}
    </RuntimeFlagGeometry>
  );
}
